import React, {useEffect, useMemo} from "react";
import {EmployeesTreeFilter, employeeTreeFilterSerializer, matchEmployeesTreeFilter} from "./EmployeesTreeFilter";
import {filterHierarchy} from "../../util/EmployeeHierarchy";
import {EmployeeTreeTable} from "./EmployeeTreeTable";
import {useEmployeeHierarchyStore} from "../../api/EmployeeHierarchyStore";
import {useFilter} from "../../util/FilterSerializer";
import {SeatingPlanModel} from "./SeatingPlanModel";
import {downloadMovedEmployeesCsv} from "./ExportMovedEmployeesCsv";
import {RemoteValue} from "../../util/RemoteValue";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {PageActionHandler} from "../../components/page-action/PageActionHandler";
import {exportCsvMenuItem} from "./actions/SeatingPlanExportMenuItem";
import {newNeighbourhoodAction} from "./actions/CreateNewSeatingPlanNeighbourhoodMenuItem";
import {SeatingPlanTab} from "./SeatingPlanTab";
import {FilterStoreProvider} from "../../components/loading-data/LoadStore";
import {ResidenceModel} from "../../api/ResidenceStore";
import {SeatingPlanStickyFilter} from "./SeatingPlanStickyFilter";
import {SeatingPlanTabLabel} from "./SeatingPlanTabLabel";


function EmployeesTreeTab({model, actionHandler, residenceModel}: {
    model: SeatingPlanModel,
    actionHandler: PageActionHandler<'exportEmployees' | 'newNeighbourhoodAction'>,
    residenceModel: ResidenceModel,
}) {
    const filterSerializer = useMemo(
        () => employeeTreeFilterSerializer(residenceModel, model.hoodItems),
        [residenceModel, model.hoodItems]
    );
    const [filter, setFilter] = useFilter(filterSerializer);
    const employeeNeighbourhood = useMemo(() => {
        return RemoteValue.fromValue(model.modifiedEmployeeNeighbourhood);
    }, [model.modifiedEmployeeNeighbourhood]);
    const employeeHierarchy = useEmployeeHierarchyStore(employeeNeighbourhood);

    const filteredHierarchy = useMemo(() => {
        return employeeHierarchy.map(({globalHierarchy}) =>
            filterHierarchy(globalHierarchy, node => matchEmployeesTreeFilter(node, filter))
        );
    }, [employeeHierarchy, filter]);

    useEffect(() => actionHandler.setHandler('exportEmployees', async () => {
        downloadMovedEmployeesCsv(model, await filteredHierarchy.value);
    }), [actionHandler, model, filteredHierarchy]);

    return (
        <>
            <SeatingPlanStickyFilter>
                <EmployeesTreeFilter filter={filter} setFilter={setFilter} allNeighbourhoods={model.hoodItems}/>
            </SeatingPlanStickyFilter>
            <LoadingData
                value={filteredHierarchy}
                defaultErrorMessage="Failed to load employees data. Please try again later."
                emptyMessage="No employees found."
            >{filteredHierarchy =>
                <EmployeeTreeTable hierarchy={filteredHierarchy} model={model}/>
            }</LoadingData>
        </>
    );
}

export const moveEmployeesTab: SeatingPlanTab<'exportEmployees' | 'newNeighbourhoodAction'> = {
    label: (model, active) => (
        <SeatingPlanTabLabel label="Employees" active={active} count={model.countMovedEmployees()}/>
    ),
    key: 'employees',
    actions: {
        exportEmployees: exportCsvMenuItem({
            label: 'Export Employees',
            tooltip: 'That match filter as CSV',
        }),
        newNeighbourhoodAction,
    },
    content: (props: {
        model: SeatingPlanModel,
        actionHandler: PageActionHandler<'exportEmployees' | 'newNeighbourhoodAction'>
    }) => (
        <FilterStoreProvider>{({residenceModel}) =>
            <EmployeesTreeTab residenceModel={residenceModel} {...props}/>
        }</FilterStoreProvider>
    ),
};
